import { EquatorialCoord } from '@hscmap/angle';
import { readFileAsBuffer } from '@/utils/file';
import { V3 } from '@/utils/math';

export async function fromCsvFile(file: File) {
  const ab = await readFileAsBuffer(file)
  return fromCsvArrayBuffer(file.name, ab)
}

export async function fromCsvArrayBuffer(name: string, ab: ArrayBuffer) {
  const bytes = new Uint8Array(ab)
  const csvRows = await parseCsvBytes(bytes)
  return compileRows(csvRows)
}

async function parseCsvBytes(bytes: Uint8Array) {
  const { parse: csvParse } = await import('@/external/csv-parse')
  if (isGzip(bytes)) {
    const gzip = await import(/* webpackChunkName: "gzip" */'gzip-js')
    bytes[2] &= ~0x01 // clear FTEXT bit
    bytes = new Uint8Array(gzip.unzip(bytes) as number[])
  }

  try {
    const array: string[][] = csvParse(new TextDecoder().decode(bytes), { relax_column_count: true })
    return array
  } catch (e) {
    alert(`CSV Parse Error: ${e}`)
    throw e
  }
}

function isGzip(b: Uint8Array) {
  return b[0] === 0x1F && b[1] === 0x8B
}

// tslint:disable-next-line:cyclomatic-complexity
export function compileRows(csvRows: string[][]) {
  let rowI
  const searchHeaderMaxRow = 100
  for (rowI = 0; rowI < searchHeaderMaxRow; ++rowI) {
    if (!csvRows[rowI][0].match(/^\s*#/)) {
      break
    }
  }
  if (rowI === searchHeaderMaxRow) {
    throw new Error(`Couldn't find a header line`)
  }

  if (rowI === 0) { // the first row is header row
    rowI = +1
  }

  let aCol = -1
  let dCol = -1
  const aCands = 'ra ra2000 a a2000'.split(' ')
  const dCands = 'dec dec2000 decl decl2000 d d2000'.split(' ')
  const header = csvRows[rowI - 1].map((h) => h.replace(/^\s*#?\s*(.*)\s*$/, '$1'))

  // rename duplicated header
  for (let i = 0; i < header.length; ++i) {
    const h = header[i]
    const j = header.indexOf(h, i + 1)
    if (j >= 0) {
      for (let k = 2; ; ++k) {
        const h2 = `${h}_${k}`
        if (header.indexOf(h2) < 0) {
          header[j] = h2
          break
        }
      }
    }
  }

  for (let j = header.length; --j >= 0;) {
    if (aCands.indexOf(header[j].toLowerCase()) >= 0) { aCol = j }
    if (dCands.indexOf(header[j].toLowerCase()) >= 0) { dCol = j }
  }
  if (aCol === -1 || dCol === -1) {
    throw new Error(`Columns for coordinates are not found: ${header.join(', ')}`)
  }

  const nCols = header.length
  const rows: any[][] = []
  const coord3: V3[] = []
  for (; rowI < csvRows.length; ++rowI) {
    const row: any[] = []
    for (let j = 0; j < nCols; ++j) {
      const cell = csvRows[rowI][j]
      const parsed = parseNumberString(cell === null ? 'null' : (cell === undefined ? '-' : cell))
      row.push(parsed.value)
    }
    const xyz = EquatorialCoord.parse(`${row[aCol]} ${row[dCol]}`).xyz
    coord3.push(xyz)
    rows.push(row)
  }
  return { header, coord3, rows }
}

function parseNumberString(s: string): { number: true, value: number } | { number: false, value: string } {
  let value = (() => {
    let m = s.match(/^(\+|-)?inf(inity)?$/i)
    if (m) {
      if (m[1] === '+' || !m[1]) {
        return Infinity
      }
      return -Infinity
    }
    m = s.match(/^nan$/i)
    if (m) {
      return NaN
    }
  })()
  if (value !== undefined) { // value == +/- infinity or nan
    return { value, number: true }
  } else {
    value = Number(s)
    if (Math.abs(value) <= Number.MAX_SAFE_INTEGER) {
      return { value, number: true }
    } else { // not a number or too large number
      return { value: s, number: false }
    }
  }
}
